import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { SESSION_TIMEOUT_EVENT, clearSessionExpiredFlag, isSessionExpiredFlagSet } from '../../utils/sessionTimeout'

const LAST_ACTIVITY_KEY = 'rs_last_activity'
const TIMEOUT_MINUTES_KEY = 'rs_session_timeout_minutes'
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']
const DEFAULT_TIMEOUT_MINUTES = 60

function readLastActivity() {
  const raw = sessionStorage.getItem(LAST_ACTIVITY_KEY)
  const value = Number(raw)
  return Number.isFinite(value) ? value : Date.now()
}

function writeLastActivity(at = Date.now()) {
  sessionStorage.setItem(LAST_ACTIVITY_KEY, String(at))
}

function clearLastActivity() {
  sessionStorage.removeItem(LAST_ACTIVITY_KEY)
}

export default function SessionTimeoutWatcher() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [expired, setExpired] = useState(() => isSessionExpiredFlagSet())
  const [timeoutMinutes, setTimeoutMinutes] = useState(() => {
    const stored = Number(sessionStorage.getItem(TIMEOUT_MINUTES_KEY))
    return Number.isFinite(stored) && stored >= 5 ? stored : DEFAULT_TIMEOUT_MINUTES
  })
  const idleTimerRef = useRef(null)
  const expiredRef = useRef(false)
  const throttleRef = useRef(0)

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
  }, [])

  const markSessionExpired = useCallback(() => {
    if (expiredRef.current) return
    expiredRef.current = true
    sessionStorage.setItem('rs_session_expired', '1')
    clearIdleTimer()
    setExpired(true)
  }, [clearIdleTimer])

  const scheduleIdleTimer = useCallback((timeoutMs) => {
    clearIdleTimer()
    const lastActivity = readLastActivity()
    const remaining = timeoutMs - (Date.now() - lastActivity)
    if (remaining <= 0) {
      markSessionExpired()
      return
    }
    idleTimerRef.current = window.setTimeout(markSessionExpired, remaining)
  }, [clearIdleTimer, markSessionExpired])

  const bumpActivity = useCallback(() => {
    if (!user || expiredRef.current) return
    const now = Date.now()
    if (now - throttleRef.current < 1000) return
    throttleRef.current = now
    writeLastActivity(now)
    scheduleIdleTimer(timeoutMinutes * 60 * 1000)
  }, [user, timeoutMinutes, scheduleIdleTimer])

  const handleSignIn = () => {
    clearIdleTimer()
    clearLastActivity()
    clearSessionExpiredFlag()
    expiredRef.current = false
    setExpired(false)
    logout()
    navigate('/login', { replace: true })
  }

  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('token')

  useEffect(() => {
    if (!user) {
      clearIdleTimer()
      expiredRef.current = false
      setExpired(false)
      return undefined
    }

    let cancelled = false
    writeLastActivity()

    axios.get('/api/auth/session-config')
      .then((res) => {
        if (cancelled) return
        const minutes = Math.max(5, Number(res.data?.sessionTimeoutMinutes) || DEFAULT_TIMEOUT_MINUTES)
        sessionStorage.setItem(TIMEOUT_MINUTES_KEY, String(minutes))
        setTimeoutMinutes(minutes)
        scheduleIdleTimer(minutes * 60 * 1000)
      })
      .catch(() => {
        if (cancelled) return
        scheduleIdleTimer(DEFAULT_TIMEOUT_MINUTES * 60 * 1000)
      })

    const onActivity = () => bumpActivity()
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true })
    })

    const onSessionTimeout = () => markSessionExpired()
    window.addEventListener(SESSION_TIMEOUT_EVENT, onSessionTimeout)

    return () => {
      cancelled = true
      clearIdleTimer()
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity)
      })
      window.removeEventListener(SESSION_TIMEOUT_EVENT, onSessionTimeout)
    }
  }, [user, bumpActivity, clearIdleTimer, markSessionExpired, scheduleIdleTimer])

  useEffect(() => {
    if (!user || expiredRef.current) return undefined
    scheduleIdleTimer(timeoutMinutes * 60 * 1000)
  }, [timeoutMinutes, user, scheduleIdleTimer])

  if (!expired || (!user && !hasToken)) return null

  return (
    <div className="session-timeout-overlay" role="presentation">
      <div
        className="session-timeout-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-timeout-title"
        aria-describedby="session-timeout-message"
      >
        <div className="session-timeout-icon" aria-hidden="true">⏱</div>
        <h2 id="session-timeout-title" className="session-timeout-title">Session Timed Out</h2>
        <p id="session-timeout-message" className="session-timeout-message">
          You have been signed out after {timeoutMinutes} minutes of inactivity. Please sign in again to continue.
        </p>
        <button type="button" className="session-timeout-btn" onClick={handleSignIn}>
          Sign In
        </button>
      </div>
    </div>
  )
}
